import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import api from '../services/api';
import MovieCard from './MovieCard';
import Loading from './Loading';
import EmptyState from './EmptyState';
import FilterSortControls from './FilterSortControls';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 20px;
`;

const MoviesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
`;

function MyMovies({ userId }) {
  const [movies, setMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Controls state
  const [filters, setFilters] = useState({ includeAdult: false, unrankedOnly: false });
  const [sortBy, setSortBy] = useState('date_added.desc'); // Default to recent (API order)

  useEffect(() => {
    const fetchRatedMovies = async () => {
      try {
        setIsLoading(true);
        const data = await api.getUserRatings(userId);
        // Filter out movies that are just 'wanna watch' or 'dismissed' (rating 0)
        const ratedMovies = data.filter(movie => movie.rating > 0);
        setMovies(ratedMovies);
      } catch (err) {
        setError('Failed to load your rated movies.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRatedMovies();
  }, [userId]);

  const handleRatingChange = (movieId, newRating) => {
    if (!newRating) {
      setMovies(prevMovies => prevMovies.filter(m => m.id !== movieId));
    } else {
      setMovies(prevMovies => prevMovies.map(m => m.id === movieId ? { ...m, rating: newRating } : m));
    }
  };

  const clearAll = () => {
    setSortBy('date_added.desc');
    setFilters({ includeAdult: false, unrankedOnly: false });
  };

  const sortedMovies = useMemo(() => {
    let sorted = [...movies];

    // If default sort (date_added), just return the list as it respects API order (created_at desc)
    if (sortBy === 'date_added.desc') {
      return sorted;
    }

    sorted.sort((a, b) => {
      const [field, order] = sortBy.split('.');
      let valA, valB;

      if (field === 'release_date') {
        valA = a.release_date ? new Date(a.release_date) : new Date(0);
        valB = b.release_date ? new Date(b.release_date) : new Date(0);
      } else if (field === 'title') {
        valA = a.title.toLowerCase();
        valB = b.title.toLowerCase();
      } else {
        return 0;
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [movies, sortBy]);

  if (isLoading) {
    return <Loading message="Loading your rated movies..." />;
  }

  if (error) {
    return <EmptyState message={error} />;
  }

  return (
    <Container>
      <Title>My Rated Movies</Title>

      <FilterSortControls
        filters={filters}
        setFilters={setFilters}
        sortBy={sortBy}
        setSortBy={setSortBy}
        clearAll={clearAll}
        hideUnranked={true}
        hideAdult={true}
        hidePopularity={true}
      />

      {sortedMovies.length > 0 ? (
        <MoviesGrid>
          {sortedMovies.map(movie => (
            <MovieCard
              key={movie.id}
              movie={movie}
              userRating={movie.rating}
              onRatingUpdate={handleRatingChange}
            />
          ))}
        </MoviesGrid>
      ) : (
        <EmptyState
          message="You haven't rated any movies yet."
          actionText="Find Movies to Rate"
          onAction={() => window.location.href = '/'}
        />
      )}
    </Container>
  );
}

export default MyMovies;